import { connect, type ConnectedProps } from "react-redux";

import type { IStoreState } from "../../../reducers/types";
import { populateFlightBookQueryParams } from "../../book/actions/actions";
import { getSelectedAccountReferenceId } from "../../rewards/reducer";
import { actions as searchActions } from "../../search/actions";
import {
  getDepartureDate,
  getDestination,
  getFareclassOptionFilter,
  getHasSetFareClassFilter,
  getOrigin,
  getReturnDate,
  getTripCategory,
} from "../../search/reducer";
import {
  fetchSelectedTripPriceFreezeOffer,
  fetchTripDetails,
  fetchTripSummaries,
  fetchTripSummariesForPredictionV2,
  listWatches,
  setChosenOutgoingSlice,
  setChosenReturnSlice,
  setOpenCalendarModal,
  setSelectedTripPriceFreezeOffer,
} from "../actions/actions";
import {
  flightShopTypeSelector,
  flightsMatchCurrentSearchSelector,
  flightsSelector,
  getSortedAndFilteredFlights,
  getTripResultEventProperties,
  getViewedForecastPropertiesForV2,
  hasSetNonFareclassFiltersSelectorV2,
  isInChooseDepartureStepSelector,
  isInChooseReturnStepSelector,
  isOutgoingMultiTicket,
  isReturnMultiTicket,
  maxFlightPriceSelectorV2,
  predictionSelector,
  refreshPredictionSelector,
  selectedTripSelector,
  tripDetailsByIdSelector,
  tripDetailsSelector,
  tripSummariesErrorSelector,
  tripSummariesLoadingSelector,
} from "../reducer";
import { FlightShopV3 } from "./component";

const mapStateToProps = (state: IStoreState) => {
  const flights = flightsSelector(state);
  const selectedTrip = selectedTripSelector(state);
  const tripId = selectedTrip.tripId || "";
  const tripDetails = tripDetailsSelector(state, tripId);

  return {
    tripSummariesLoading: tripSummariesLoadingSelector(state),
    hasFlightsError: tripSummariesErrorSelector(state) || false,
    flights,
    flightsMatchSearch: flightsMatchCurrentSearchSelector(state),
    rewardsKey: getSelectedAccountReferenceId(state) ?? undefined,
    fareClassFilter: getFareclassOptionFilter(state),
    // isRoundTrip is derived in the component from this plus the (authoritative)
    // URL query, so the ChooseReturn step is never skipped while the search store
    // is still hydrating from the URL. See isRoundTripSelection.
    tripCategory: getTripCategory(state),
    prediction: predictionSelector(state),
    origin: getOrigin(state),
    destination: getDestination(state),
    tripDetailsById: tripDetailsByIdSelector(state),
    departureDate: getDepartureDate(state),
    returnDate: getReturnDate(state),
    flightList: getSortedAndFilteredFlights(state),
    viewedForecastProperties: getViewedForecastPropertiesForV2(state),
    tripResultEventProperties: getTripResultEventProperties(state),
    selectedTrip,
    maxFlightPrice: maxFlightPriceSelectorV2(state),
    hasAppliedFareClassFilter: getHasSetFareClassFilter(state),
    hasAppliedNonFareclassFilter: hasSetNonFareclassFiltersSelectorV2(state),
    flightShopType: flightShopTypeSelector(state),
    refreshPrediction: refreshPredictionSelector(state),
    isMultiTicket: isOutgoingMultiTicket(state) || isReturnMultiTicket(state),
    isInChooseDepartureStep: isInChooseDepartureStepSelector(state),
    isInChooseReturnStep: isInChooseReturnStepSelector(state),
    tripDetails,
  };
};

const mapDispatchToProps = {
  fetchTripSummaries,
  fetchTripDetails,
  setSelectedTripPriceFreezeOffer,
  setChosenReturnSlice,
  setChosenOutgoingSlice,
  setOpenCalendarModal,
  setAirlineFilter: searchActions.setAirlineFilter,
  setStopsOption: searchActions.setStopsOption,
  setFlightNumberFilter: searchActions.setFlightNumberFilter,
  setOutboundArrivalTimeRange: searchActions.setOutboundArrivalTimeRange,
  setOutboundDepartureTimeRange: searchActions.setOutboundDepartureTimeRange,
  setReturnDepartureTimeRange: searchActions.setReturnDepartureTimeRange,
  setReturnArrivalTimeRange: searchActions.setReturnArrivalTimeRange,
  setMaxPriceFilter: searchActions.setMaxPriceFilter,
  setFareclassOptionFilter: searchActions.setFareclassOptionFilter,
  setAirportFilter: searchActions.setAirportFilter,
  populateFlightBookQueryParams,
  fetchTripSummariesForPrediction: fetchTripSummariesForPredictionV2,
  listWatches,
  fetchSelectedTripPriceFreezeOffer,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export type FlightShopConnectorProps = ConnectedProps<typeof connector>;

export const ConnectedFlightShop = connector(FlightShopV3);
